import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgxMaskDirective } from 'ngx-mask';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MaterialModule } from '../../../shared/material/material.module';
import {
  ProfileProviderPF,
  ProfileProviderPfControllerService,
  UserRef,
  CouncilControllerService,
  Council,
  AttachmentControllerService,
  Attachment,
} from '../../../core/generated';
import { CouncilComponent } from './council/council.component';
import { MatDialog } from '@angular/material/dialog';
import { DialogCNESFinderComponent } from '../../../shared/dialogs/dialog-cnes-finder/dialog-cnes-finder.component';
import { CNES } from '../../../shared/dialogs/dialog-cnes-finder/cnes-finder.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AttachmentComponent } from '../../../shared/attachment/attachment.component';
import { register, SwiperContainer } from 'swiper/element';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthCustomService } from '../../../security/auth-custom.service';
import { maskConfig } from '../../../shared/maskConfiguration';
import { ProfileProviderPFStateService } from '../../../shared/services/profile-provider-pf-state.service';
import { PlanStatusService } from '../../../shared/services/plan-status-cache.service';
import { firstValueFrom } from 'rxjs';
import { AddressDialogComponent } from '../../../shared/address-dialog/address-dialog.component';
import { AddressListComponent } from '../../../shared/address-list/address-list.component';
import { EncryptionUtil } from '../../../shared/utils/encryption.util';
import { CertificationButtonComponent } from '../../../shared/certification-button/certification-button.component';
import { EnumDescriptionPipe } from '../../../shared/pipes/enum-description.pipe';
import { ContainerComponent } from '../../../shared/container/container.component';
register();

@Component({
    selector: 'app-profile-provider-pf',
    standalone: true,
    imports: [
        MaterialModule,
        CouncilComponent,
        AttachmentComponent,
        FormsModule,
        NgxMaskDirective,
        AddressListComponent,
        ReactiveFormsModule,
        CertificationButtonComponent,
        EnumDescriptionPipe,
        ContainerComponent
    ],
    templateUrl: './profile-provider-pf.component.html',
    styleUrl: './profile-provider-pf.component.scss',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProfileProviderPFComponent implements OnInit, AfterViewInit {
  private profileProviderPFService = inject(ProfileProviderPfControllerService);
  private _snackBar = inject(MatSnackBar);
  private profileProviderPFStateService = inject(ProfileProviderPFStateService);
  private cd = inject(ChangeDetectorRef);
  private fb = inject(FormBuilder);
  private authCustomService = inject(AuthCustomService);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private councilService = inject(CouncilControllerService);
  private attachmentsService = inject(AttachmentControllerService);

  isMobile = inject(DeviceDetectorService).isMobile();
  router: Router = inject(Router);
  planStatusService = inject(PlanStatusService);

  selectedTab = new FormControl('');
  @ViewChild('swiper') swiperRef!: ElementRef<SwiperContainer>;
  providerPFForm: FormGroup = this.fb.group({
    id: [null],
    name: [{ value: '', disabled: true }, Validators.required],
    cpf: [],
    email: [{ value: '', disabled: true }, Validators.required],
    phoneNumber: [{ value: '', disabled: true }, Validators.required],
    cnesCode: [{ value: '', disabled: true }, Validators.required],
    registerDate: [{ value: '', disabled: true }],
    active: [{ value: true, disabled: true }],
    certifiedHubMedical: [{ value: false, disabled: true }],
    status: [{ value: '', disabled: true }],
  });
  profileProviderPF!: ProfileProviderPF;
  profileProviderPF_ID: string | null = null;
  isEditMode = false;
  hasAnyProfileCouncil = false;
  showProfessionForm = false;
  user!: UserRef;
  documentTypesRequired: string[] = ["CRM", "Identidade"];
  maskConfig = maskConfig;
  addresses: any[] = [];
  selectedAddressId: number | null = null;
  isLoading = true;
  payerCnpj = null;
  productPackageType: string | null = null;

  // Estados de certificação
  isCertifiedCouncilAndProfession: boolean = false;
  isCertifiedDocuments: boolean = false;

  async ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id');
    this.profileProviderPF_ID = id;
    let loaded = false;
    if (id) {
      loaded = await this.loadProviderPFFormById(id);
      if (loaded) {
        this.profileProviderPFStateService.loadById(Number(id));
        await this.tryLoadAddresses(this.profileProviderPF?.id);
      }
    } else {
      const useEmail = this.authCustomService.userEmail;
      loaded = await this.loadProviderPFFormByUserEmail(useEmail);
      if (loaded) {
        this.profileProviderPFStateService.loadByEmail(useEmail);
        await this.tryLoadAddresses(this.profileProviderPF?.id);
        this.router.navigate(['my-account/profiles/profileProviderPFForm/' + this.profileProviderPF.id]);
      }
    }
    this.editMode(!this.providerPFForm.controls['id'].value);

    this.route.queryParams.subscribe(params => {
      if (!this.providerPFForm.controls['id'].value) {
        if (params['pkg']) {
          const decryptedPackage = EncryptionUtil.decrypt(params['pkg']);
          if (decryptedPackage) {
            this.productPackageType = decryptedPackage;
            this.cd.markForCheck();
          } else {
            console.warn('Parâmetro packageType inválido ou manipulado');
            this.router.navigate(['/my-account/profiles/select-new-profile']);
          }
        } else {
          this.router.navigate(['/my-account/profiles/select-new-profile']);
        }
      }
    });

    this.selectedTab.valueChanges.subscribe((value) => {
      const tabIndex = ['tab1', 'tab2', 'tab3', 'tab4', 'tab5'].indexOf(value ?? '');
      if (tabIndex >= 0) this.swiperRef.nativeElement.swiper.slideTo(tabIndex);
    });

    this.isLoading = false;
    this.cd.markForCheck();

    if (this.providerPFForm.controls['id'].value) {
      this.refreshCertifications();
    }
  }

  ngAfterViewInit(): void {
    this.initializeSwiperWithRetry();
    
    this.route.queryParams.subscribe(params => {
      const requestedTab = params['tab'] || 'tab1';
      if (!this.providerPFForm.controls['id'].value && requestedTab !== 'tab1') {
        this.selectedTab.setValue('tab1');
      } else {
        this.selectedTab.setValue(requestedTab);
      }
    });
  }

  private initializeSwiperWithRetry(attempts = 0, maxAttempts = 10) {
    setTimeout(() => {
      const swiperInstance = this.swiperRef?.nativeElement?.swiper;
      
      if (swiperInstance) {
        this.setupSwiperListeners(swiperInstance);
      } else {
        if (attempts < maxAttempts) {
          this.initializeSwiperWithRetry(attempts + 1, maxAttempts);
        }
      }
    }, 200 * (attempts + 1));
  }

  private setupSwiperListeners(swiperInstance: any) {
    this.selectedTab.valueChanges.subscribe((value) => {
      if (!this.providerPFForm?.controls?.['id']?.value && value !== 'tab1') {
        this.selectedTab.setValue('tab1', { emitEvent: false });
        swiperInstance.slideTo(0);
        return;
      }

      const index = ({
        tab1: 0,
        tab2: 1,
        tab3: 2,
        tab4: 3,
        tab5: 4,
      } as Record<string, number>)[value ?? 'tab1'];

      if (typeof index === 'number' && swiperInstance.activeIndex !== index) {
        swiperInstance.slideTo(index);
      }
    });

    swiperInstance.on('slideChange', () => {
      const idx = swiperInstance.activeIndex;
      const tabValue = ['tab1', 'tab2', 'tab3', 'tab4', 'tab5'][idx] || 'tab1';

      if (!this.providerPFForm?.controls?.['id']?.value && tabValue !== 'tab1') {
        this.selectedTab.setValue('tab1', { emitEvent: false });
        swiperInstance.slideTo(0);
      } else {
        if (this.selectedTab.value !== tabValue) {
          this.selectedTab.setValue(tabValue, { emitEvent: false });

          if (tabValue === 'tab5' && this.providerPFForm.controls['id'].value) {
            this.refreshCertifications();
          }

          this.cd.markForCheck();
        }
      }
    });
  }

  findCNES() {
    const dialogRef = this.dialog.open(DialogCNESFinderComponent, { data: { cns: 123456 } });
    dialogRef.afterClosed().subscribe();
  }

  private async tryLoadAddresses(profileId?: number) {
    if (profileId) await this.loadAddressesByProfileProviderPFId(profileId);
    else console.warn('ProfileProviderPF ID is undefined.');
  }

  loadProviderPFFormById(id: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.profileProviderPFService.findById7(Number(id)).subscribe(
        (providerPF) => {
          if (providerPF) {
            this.profileProviderPF = providerPF;
            this.providerPFForm.patchValue(providerPF);
            resolve(true);
          } else resolve(false);
        },
        () => resolve(false)
      );
    });
  }

  loadProviderPFFormByUserEmail(userEmail: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.profileProviderPFService.findByUserEmail1(userEmail).subscribe(
        (providerPF) => {
          if (providerPF) {
            this.profileProviderPF = providerPF;
            this.providerPFForm.patchValue(providerPF);
            resolve(true);
          } else resolve(false);
        },
        () => resolve(false)
      );
    });
  }

  async saveProfileProviderPF() {
    if (!this.providerPFForm.valid) return;
    const formValue = this.providerPFForm.value;
    const isNew = formValue.id == null;
    const serviceCall = isNew
      ? this.profileProviderPFService.save6(
          this.productPackageType as 'SUBSCRIPTION_PROVIDER_PF_FREE' | 'SUBSCRIPTION_PROVIDER_PF_BASIC' | 'SUBSCRIPTION_LAB_FREE' | 'SUBSCRIPTION_LAB_BASIC' | 'SUBSCRIPTION_PROVIDER_PJ_FREE' | 'SUBSCRIPTION_PROVIDER_PJ_2' | 'SUBSCRIPTION_PROVIDER_PJ_6' | 'SUBSCRIPTION_PROVIDER_PJ_10' | 'SUBSCRIPTION_PROVIDER_PJ_PERSONAL' | 'SUBSCRIPTION_PROVIDER_CONTRACTOR_PJ_FREE' | 'SUBSCRIPTION_PROVIDER_CONTRACTOR_PJ_100' | 'SUBSCRIPTION_PROVIDER_CONTRACTOR_PJ_PERSONAL' | 'SUBSCRIPTION_CONTRACTOR_PJ_120' | 'SUBSCRIPTION_CONTRACTOR_PJ_PERSONAL' | 'SUBSCRIPTION_ADMINISTRATOR',
          formValue,
          this.payerCnpj ?? undefined
        )
      : this.profileProviderPFService.update6(formValue.id, formValue);
    await serviceCall.subscribe(async (response) => {
      this.providerPFForm.patchValue(response);

      if (isNew) {
        this.profileProviderPFStateService.setProfile(response);
        if (response.cpf) {
          await this.planStatusService.refresh();
        }
      } else {
        this.profileProviderPFStateService.updateProfile(response);
      }

      this.refreshCertifications();
      this.cd.markForCheck();
      this.router.navigate(['my-account/profiles/profileProviderPFForm/' + response.id]);
    });
    this.editMode(false);
  }

  editMode(editMode: boolean) {
    this.isEditMode = editMode;
    const controls = this.providerPFForm.controls;
    ['name', 'email', 'phoneNumber'].forEach(field => controls[field][editMode ? 'enable' : 'disable']());
    if (editMode) {
      controls['certifiedHubMedical'].value ? controls['cnesCode'].disable() : controls['cnesCode'].enable();
    } else {
      controls['cnesCode'].disable();
    }
  }

  hasAnyProfileCouncilFinder() {
    this.hasAnyProfileCouncil = false;
  }

  isReadyToCertify() {
    return true;
  }

  async loadAddressesByProfileProviderPFId(profileProviderPFId: number) {
    const response = await firstValueFrom(this.profileProviderPFService.getAllAddresses1(profileProviderPFId));
    this.addresses = response || [];
  }

  async createAddress(address: any) {
    const id = Number(this.profileProviderPF_ID ?? '') || this.profileProviderPF.id!;
    address.profileProviderPF = this.profileProviderPF;
    const newAddress = await firstValueFrom(this.profileProviderPFService.addAddress1(id, address));
    if (newAddress) {
      this.addresses = [...this.addresses, newAddress];
      this.cd.markForCheck();
    }
  }

  async deleteAddress(addressId: number) {
    const id = this.providerPFForm?.controls['id']?.value ? Number(this.providerPFForm.controls['id'].value) : this.profileProviderPF?.id;
    if (!id) {
      this._snackBar.open('ID do perfil não encontrado. Não é possível remover endereço.', '', { duration: 3000 });
      return;
    }
    await firstValueFrom(this.profileProviderPFService.deleteAddress1(id, addressId));
    this.addresses = this.addresses.filter(address => address.id !== addressId);
    this._snackBar.open('Endereço removido com sucesso', '', { duration: 3000 });
    this.cd.markForCheck();
  }

  async setCommercialAddress(addressId: number) {
    this.profileProviderPFService.setCommercialAddress1(this.profileProviderPF.id!, addressId).subscribe(
      (updatedAddress) => {
        this.addresses.forEach(address => address.commercial = address.id === addressId ? updatedAddress.commercial : false);
      }
    );
  }

  openAddressDialog() {
    if (this.providerPFForm.controls['id'].value === null) {
      this._snackBar.open('Perfil não carregado. Não é possível adicionar endereço.', '', { duration: 3000 });
      return;
    }
    const dialogRef = this.dialog.open(AddressDialogComponent, {
      data: { addresses: this.addresses, profileProviderPFId: this.providerPFForm.controls['id'].value }
    });
    dialogRef.afterClosed().subscribe((newAddress: any) => { if (newAddress) this.createAddress(newAddress); });
  }

  async editAddress(addressId: number) {
    const addressToEdit = this.addresses.find(address => address.id === addressId);
    if (!addressToEdit) {
      console.error('Address not found:', addressId);
      return;
    }
    const dialogRef = this.dialog.open(AddressDialogComponent, {
      data: { address: addressToEdit }
    });
    dialogRef.afterClosed().subscribe((updatedAddress: any) => {
      console.log('Dialog result:', updatedAddress);
      if (updatedAddress) {
        this.profileProviderPFService.updateAddress1(this.profileProviderPF.id!, addressId, updatedAddress).subscribe(
          (response) => {
            const index = this.addresses.findIndex(address => address.id === addressId);
            if (index !== -1) {
              this.addresses = this.addresses.map((a, i) => i === index ? response : a);
              this.cd.markForCheck();
            }
          }
        );
      }
    });
  }

  checkCertifiedCompanyData(): boolean {
    return this.providerPFForm.controls['certifiedHubMedical'].value;
  }

  checkCertifiedCouncilAndProfession(): void {
    if (!this.profileProviderPF?.id) {
      this.isCertifiedCouncilAndProfession = false;
      return;
    }

    this.councilService.findAllByProfileProviderPFId(this.profileProviderPF.id).subscribe(
      (councils: Council[]) => {
        if (!councils || councils.length === 0) {
          this.isCertifiedCouncilAndProfession = false;
          this.cd.markForCheck();
          return;
        }

        let allCertified = true;
        for (const council of councils) {
          if (!council.certifiedHubMedical) {
            allCertified = false;
            break;
          }

          if (!council.councilProfession || council.councilProfession.size === 0) {
            allCertified = false;
            break;
          }

          for (const profession of Array.from(council.councilProfession)) {
            if (!profession.certifiedHubMedical) {
              allCertified = false;
              break;
            }
          }

          if (!allCertified) {
            break;
          }
        }

        this.isCertifiedCouncilAndProfession = allCertified;
        this.cd.markForCheck();
      },
      (error: any) => {
        console.error('Erro ao buscar conselhos:', error);
        this.isCertifiedCouncilAndProfession = false;
        this.cd.markForCheck();
      }
    );
  }

  checkCertifiedDocuments(): void {
    if (!this.profileProviderPF?.cpf) {
      this.isCertifiedDocuments = false;
      return;
    }

    this.attachmentsService.getFilesListByOwner(this.profileProviderPF.cpf).subscribe(
      (attachments: Attachment[]) => {
        if (!attachments || attachments.length === 0) {
          this.isCertifiedDocuments = false;
          this.cd.markForCheck();
          return;
        }

        let allRequiredDocsCertified = true;
        
        for (const requiredDocType of this.documentTypesRequired) {
          const doc = attachments.find(att => att.documentType === requiredDocType);
          
          if (!doc || !doc.certifiedHubMedical) {
            allRequiredDocsCertified = false;
            break;
          }
        }

        this.isCertifiedDocuments = allRequiredDocsCertified;
        this.cd.markForCheck();
      },
      (error: any) => {
        console.error('Erro ao buscar documentos:', error);
        this.isCertifiedDocuments = false;
        this.cd.markForCheck();
      }
    );
  }

  checkAllCertification(): boolean {
    return this.checkCertifiedCompanyData() && this.isCertifiedCouncilAndProfession && this.isCertifiedDocuments;
  }

  refreshCertifications(): void {
    this.checkCertifiedCouncilAndProfession();
    this.checkCertifiedDocuments();
  }
}


